import { ApolloError } from '@apollo/client';
import { Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { getUserFacingErrorMessages } from '@/graphql/errors';
import { useReportError } from '@/hooks/useReportError';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';

import {
  useAdminSecurityPageAddManagedSftpPublicKeyMutation,
  useAdminSecurityPageDisableManagedSftpMutation,
  useAdminSecurityPageEnableManagedSftpMutation,
  useAdminSecurityPageManagedSftpQuery,
  useAdminSecurityPageRevokeManagedSftpPublicKeyMutation,
} from '../AdminSecurityPage/graphql/ManagedSFTP.generated';
import { ManagedSFTPSettingsContent } from './ManagedSFTPSettingsContent';

const mutationOptions = {
  fetchPolicy: 'no-cache',
  errorPolicy: 'none',
} as const;

export function ManagedSFTPSettings() {
  const [confirmation, setConfirmation] = useState<
    { kind: 'disable' } | { kind: 'remove'; keyID: string }
  >();
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const [saving, setSaving] = useState(false);
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [outcomeMessage, setOutcomeMessage] = useState<string>();
  const { data, loading, error, refetch } =
    useAdminSecurityPageManagedSftpQuery({
      // Key IDs are tenant-scoped AWS strings, not globally unique Node IDs.
      fetchPolicy: 'no-cache',
      errorPolicy: 'none',
    });
  const [enable, { loading: enableLoading }] =
    useAdminSecurityPageEnableManagedSftpMutation(mutationOptions);
  const [addKey, { loading: addLoading }] =
    useAdminSecurityPageAddManagedSftpPublicKeyMutation(mutationOptions);
  const [removeKey, { loading: removeLoading }] =
    useAdminSecurityPageRevokeManagedSftpPublicKeyMutation(mutationOptions);
  const [disable, { loading: disableLoading }] =
    useAdminSecurityPageDisableManagedSftpMutation(mutationOptions);
  const connection = data?.managedSFTP ?? null;
  const mutationLoading =
    saving || addLoading || removeLoading || disableLoading;

  const refresh = async () => {
    try {
      await refetch();
      setNeedsRefresh(false);
      if (needsRefresh) setOutcomeMessage(undefined);
      return true;
    } catch (refreshError) {
      setNeedsRefresh(true);
      reportError(
        'Could not read current managed SFTP settings',
        refreshError as Error
      );
      return false;
    }
  };

  const save = async (
    mutation: () => Promise<boolean>,
    successMessage: string,
    errorDescription: string
  ) => {
    setSaving(true);
    setOutcomeMessage(undefined);
    try {
      if (!(await mutation()))
        throw new Error('SFTP command was not confirmed');
      if (await refresh()) {
        showFeedback(successMessage, { variant: 'success' });
      } else {
        setOutcomeMessage(
          `${successMessage} Current settings could not be loaded. Refresh before making another change.`
        );
      }
      return true;
    } catch (mutationError) {
      const messages =
        mutationError instanceof ApolloError
          ? getUserFacingErrorMessages(mutationError)
          : [];
      showFeedback(messages[0] ?? 'Could not confirm the SFTP change.', {
        variant: 'error',
      });
      reportError(errorDescription, mutationError as Error);
      setOutcomeMessage(
        'The change could not be confirmed. Review the current settings before retrying.'
      );
      await refresh();
      return false;
    } finally {
      setConfirmation(undefined);
      setSaving(false);
    }
  };

  if (loading) {
    return <Typography variant="body-xs">Loading managed SFTP…</Typography>;
  }
  if (error || needsRefresh) {
    return (
      <Stack spacing={2} alignItems="flex-start">
        <Callout severity="error">
          {outcomeMessage ??
            'Managed SFTP settings are unavailable. Refresh to read the current state.'}
        </Callout>
        <Button
          size="sm"
          variant="secondary"
          loading={saving}
          onClick={refresh}
        >
          Refresh settings
        </Button>
      </Stack>
    );
  }

  return (
    <>
      {outcomeMessage && <Callout severity="warning">{outcomeMessage}</Callout>}
      <ManagedSFTPSettingsContent
        filesHref={getCompletePathFromRouteKey('ADMIN_SFTP_FILES', {})}
        connection={connection}
        enableLoading={enableLoading || saving}
        mutationLoading={mutationLoading}
        onEnable={() =>
          save(
            async () => Boolean((await enable()).data?.enableManagedSFTP.id),
            'Managed SFTP enabled.',
            'Could not enable managed SFTP'
          )
        }
        onAddKey={(publicKeyText) =>
          connection
            ? save(
                async () =>
                  Boolean(
                    (
                      await addKey({
                        variables: {
                          input: { publicKeyText },
                        },
                      })
                    ).data?.addManagedSFTPPublicKey.id
                  ),
                'SSH public key added.',
                'Could not add managed SFTP public key'
              )
            : Promise.resolve(false)
        }
        onRemoveKey={(key) =>
          setConfirmation({ kind: 'remove', keyID: key.id })
        }
        onDisable={() => setConfirmation({ kind: 'disable' })}
      />

      {confirmation && connection && (
        <ConfirmationModal
          isOpen
          heading={
            confirmation.kind === 'remove'
              ? 'Remove SSH public key?'
              : 'Disable managed SFTP?'
          }
          confirmText={
            confirmation.kind === 'remove' ? 'Remove key' : 'Disable SFTP'
          }
          confirmButtonProps={{
            variant: 'destructive-primary',
            loading: mutationLoading,
          }}
          closeOnOutsideClick={!mutationLoading}
          onClose={async (reason) => {
            if (reason === 'cancel') return setConfirmation(undefined);
            const selectedKey =
              confirmation.kind === 'remove'
                ? connection.publicKeys.find(
                    (key) => key.id === confirmation.keyID
                  )
                : undefined;
            const removing = confirmation.kind === 'remove' && selectedKey;
            if (confirmation.kind === 'remove' && !selectedKey) {
              setConfirmation(undefined);
              return;
            }
            const mutation = async () =>
              removing
                ? (
                    await removeKey({
                      variables: { input: { id: removing.id } },
                    })
                  ).data?.revokeManagedSFTPPublicKey === true
                : Boolean((await disable()).data?.disableManagedSFTP.id);
            await save(
              mutation,
              removing ? 'SSH public key removed.' : 'Managed SFTP disabled.',
              removing
                ? 'Could not remove managed SFTP public key'
                : 'Could not disable managed SFTP'
            );
          }}
        >
          {confirmation.kind === 'remove'
            ? 'Future logins with this key will be blocked. Existing sessions will not end.'
            : 'This removes all registered keys and blocks future logins. Existing sessions will not end. If you enable SFTP again, register your keys again.'}
        </ConfirmationModal>
      )}
    </>
  );
}
